import moment from 'moment';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';


/*
    All action types constants should include "Dialog" substring if you use them to open/close dialog windows
    Use "open" & "close" substrings inside action types for correct middleware work.
*/


export interface State {
    isRulesDialogOpen: boolean,
    isBoosterDialogOpen: boolean,
    isWelcomeDialogOpen: boolean,
    isErrorDialogOpen: boolean,
    isVideoPlayerVisible: boolean,
    isBuyoutDialogOpen: boolean,
    isFinalDialogOpen: boolean,
    error: Error | null,
    isOverlayVisible: boolean,
    isWelcomeMessageVisible: boolean,
    isMultipleBuyDialogVisible: boolean,
    isStartBuyDialogVisible: boolean,
    isProgressBarPortalVisible: boolean,
    isWalletPortalVisible: boolean,
    isLoadErrorVisible: boolean,
    isBuyGoldDialogVisible: boolean,
    isShipFeaturesDialogVisible: boolean,
    shipFeaturesDialogShipId: number | null,

    videoCloseTime: number,
    finalDialogShipName: string,

    isBlueprintsViewerVisible: boolean,
    isDownloadGamePopupVisible: boolean,
    isBundleBuyDialogVisible: boolean,
}

const initialState: State = {
    isRulesDialogOpen: false,
    isBoosterDialogOpen: false,
    isWelcomeDialogOpen: false,
    isErrorDialogOpen: false,
    isVideoPlayerVisible: false,
    isBuyoutDialogOpen: false,
    isFinalDialogOpen: false,
    error: null,
    isOverlayVisible: false,
    isWelcomeMessageVisible: false,
    isMultipleBuyDialogVisible: false,
    isStartBuyDialogVisible: false,
    isProgressBarPortalVisible: false,
    isWalletPortalVisible: false,
    isLoadErrorVisible: false,
    isBuyGoldDialogVisible: false,
    isShipFeaturesDialogVisible: false,
    shipFeaturesDialogShipId: null,

    videoCloseTime: 0,
    finalDialogShipName: '',

    isBlueprintsViewerVisible: false,
    isDownloadGamePopupVisible: false,
    isBundleBuyDialogVisible: false,
};

const dialogsSlice = createSlice({
    name: 'dialogs',
    initialState,
    reducers: {
        openBuyGoldDialog(state: State) {
            state.isBuyGoldDialogVisible = true;
        },
        closeBuyGoldDialog(state: State) {
            state.isBuyGoldDialogVisible = false;
        },

        openWalletPortal(state: State) {
            state.isWalletPortalVisible = true;
        },
        closeWalletPortal(state: State) {
            state.isWalletPortalVisible = false;
        },

        openProgressBarPortal(state: State) {
            state.isProgressBarPortalVisible = true;
        },
        closeProgressBarPortal(state: State) {
            state.isProgressBarPortalVisible = false;
        },

        showOverlay(state: State) {
            state.isOverlayVisible = true;
        },
        hideOverlay(state: State) {
            state.isOverlayVisible = false;
        },

        showVideoPlayer(state: State) {
            state.isVideoPlayerVisible = true;
        },
        hideVideoPlayer(state: State) {
            state.isVideoPlayerVisible = false;
            state.videoCloseTime = moment.utc().unix() * 1000;
        },

        openFinalDialog(state: State, action: PayloadAction<string>) {
            const newState = {
                isRulesDialogOpen: false,
                isBoosterDialogOpen: false,
                isWelcomeDialogOpen: false,
                isErrorDialogOpen: false,
                isBuyoutDialogOpen: false,
                isFinalDialogOpen: true,
                isMultipleBuyDialogVisible: false,
                isStartBuyDialogVisible: false,
                isShipFeaturesDialogVisible: false,
                finalDialogShipName: action.payload,
            };
            Object.assign(state, newState);
        },
        closeFinalDialog(state: State) {
            state.isFinalDialogOpen = false;
        },

        openBuyoutDialog(state: State) {
            const newState = {
                isRulesDialogOpen: false,
                isBoosterDialogOpen: false,
                isWelcomeDialogOpen: false,
                isErrorDialogOpen: false,
                isBuyoutDialogOpen: true,
                isFinalDialogOpen: false,
                isMultipleBuyDialogVisible: false,
                isStartBuyDialogVisible: false,
                isShipFeaturesDialogVisible: false,
            };
            Object.assign(state, newState);
        },
        closeBuyoutDialog(state: State) {
            state.isBuyoutDialogOpen = false;
        },

        openErrorDialog(state: State, action: PayloadAction<Error>) {
            const newState = {
                isRulesDialogOpen: false,
                isBoosterDialogOpen: false,
                isWelcomeDialogOpen: false,
                isErrorDialogOpen: true,
                isBuyoutDialogOpen: false,
                isFinalDialogOpen: false,
                error: action.payload,
                isMultipleBuyDialogVisible: false,
                isStartBuyDialogVisible: false,
                isShipFeaturesDialogVisible: false,
            };
            Object.assign(state, newState);
        },
        closeErrorDialog(state: State) {
            state.isErrorDialogOpen = false;
            state.error = null;
        },

        openWelcomeDialog(state: State, action: PayloadAction<boolean>) {
            const newState = {
                isRulesDialogOpen: false,
                isBoosterDialogOpen: false,
                isWelcomeDialogOpen: true,
                isErrorDialogOpen: false,
                isBuyoutDialogOpen: false,
                isFinalDialogOpen: false,
                isWelcomeMessageVisible: action.payload,
                isMultipleBuyDialogVisible: false,
                isStartBuyDialogVisible: false,
                isShipFeaturesDialogVisible: false,
            };
            Object.assign(state, newState);
        },
        closeWelcomeDialog(state: State) {
            state.isWelcomeDialogOpen = false;
            state.isWelcomeMessageVisible = true;
        },

        openBoosterDialog(state: State) {
            const newState = {
                isBoosterDialogOpen: true,
                isRulesDialogOpen: false,
                isWelcomeDialogOpen: false,
                isErrorDialogOpen: false,
                isBuyoutDialogOpen: false,
                isFinalDialogOpen: false,
                isMultipleBuyDialogVisible: false,
                isStartBuyDialogVisible: false,
                isShipFeaturesDialogVisible: false,
            };
            Object.assign(state, newState);
        },
        closeBoosterDialog(state: State) {
            state.isBoosterDialogOpen = false;
        },

        openRulesDialog(state: State) {
            const newState = {
                isRulesDialogOpen: true,
                isBoosterDialogOpen: false,
                isWelcomeDialogOpen: false,
                isErrorDialogOpen: false,
                isBuyoutDialogOpen: false,
                isFinalDialogOpen: false,
                isMultipleBuyDialogVisible: false,
                isStartBuyDialogVisible: false,
                isShipFeaturesDialogVisible: false,
            };
            Object.assign(state, newState);
        },
        closeRulesDialog(state: State) {
            state.isRulesDialogOpen = false;
        },

        openMultipleBuyDialog(state: State) {
            const newState = {
                isRulesDialogOpen: false,
                isBoosterDialogOpen: false,
                isWelcomeDialogOpen: false,
                isErrorDialogOpen: false,
                isBuyoutDialogOpen: false,
                isFinalDialogOpen: false,
                isMultipleBuyDialogVisible: true,
                isStartBuyDialogVisible: false,
                isShipFeaturesDialogVisible: false,
            };
            Object.assign(state, newState);
        },
        closeMultipleBuyDialog(state: State) {
            state.isMultipleBuyDialogVisible = false;
        },

        openStartBuyDialog(state: State) {
            const newState = {
                isRulesDialogOpen: false,
                isBoosterDialogOpen: false,
                isWelcomeDialogOpen: false,
                isErrorDialogOpen: false,
                isBuyoutDialogOpen: false,
                isFinalDialogOpen: false,
                isMultipleBuyDialogVisible: false,
                isStartBuyDialogVisible: true,
                isShipFeaturesDialogVisible: false,
            };
            Object.assign(state, newState);
        },
        closeStartBuyDialog(state: State) {
            state.isStartBuyDialogVisible = false;
        },

        showLastChanceScreen() {
            // @TODO: [legacy?] no last chance screen found
        },

        showLoadError(state: State) {
            state.isLoadErrorVisible = true;
        },
        hideLoadError(state: State) {
            state.isLoadErrorVisible = false;
        },

        showBlueprintsViewer(state: State) {
            state.isBlueprintsViewerVisible = true;
        },
        hideBlueprintsViewer(state: State) {
            state.isBlueprintsViewerVisible = false;
        },

        showDownloadGamePopup(state: State) {
            state.isDownloadGamePopupVisible = true;
        },
        hideDownloadGamePopup(state: State) {
            state.isDownloadGamePopupVisible = false;
        },

        openBundleBuyDialog(state: State) {
            state.isBundleBuyDialogVisible = true;
        },
        closeBundleBuyDialog(state: State) {
            state.isBundleBuyDialogVisible = false;
        },

        openShipFeaturesDialog: {
            reducer(state: State, action: PayloadAction<{shipId: number}>) {
                state.shipFeaturesDialogShipId = Number(action.payload.shipId);
                state.isShipFeaturesDialogVisible = true;
            },
            prepare(shipId: number | string) {
                return { payload: { skipMiddleware: true, shipId: Number(shipId) } };
            },
        },
        closeShipFeaturesDialog: {
            reducer(state: State) {
                state.shipFeaturesDialogShipId = null;
                state.isShipFeaturesDialogVisible = false;
            },
            prepare() {
                return { payload: { skipMiddleware: true } };
            },
        },
    },
});

export default dialogsSlice.reducer;


// All reducers for actions/middleware
export const dialogsReducerActions = dialogsSlice.actions;
